import { Bars3Icon } from "@heroicons/react/20/solid";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { Sidebar } from "@/components/Sidebar";
import { ErrorState, PageSpinner } from "@/components/ui";
import { api, errorMessage, isStatus } from "@/lib/api";
import { keys, useMe } from "@/lib/queries";
import { cn } from "@/lib/utils";

export function RequireAuth() {
  const me = useMe();
  const location = useLocation();
  const setup = useQuery({ queryKey: keys.setup, queryFn: api.setupStatus, enabled: isStatus(me.error, 401) });

  if (me.isPending) return <PageSpinner />;
  if (me.error) {
    if (!isStatus(me.error, 401)) return <ErrorState message={errorMessage(me.error)} onRetry={() => me.refetch()} />;
    if (setup.isPending) return <PageSpinner />;
    if (setup.data?.needsSetup) return <Navigate to="/setup" replace />;
    const next = location.pathname + location.search;
    return <Navigate to={next === "/" ? "/login" : `/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

/** Page chrome: sidebar plus scrollable main area. */
export function Shell({ sidebar, children }: { sidebar: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="flex h-full">
      <div
        className={cn("fixed inset-0 z-30 bg-black/40 md:hidden", open ? "block" : "hidden")}
        onClick={() => setOpen(false)}
      />
      <aside
        className={cn(
          "fixed inset-y-0 start-0 z-30 w-64 shrink-0 border-e border-line bg-sidebar transition-transform md:static md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full",
        )}
      >
        {sidebar}
      </aside>
      <main className="relative flex min-w-0 flex-1 flex-col">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute top-2.5 start-2 z-20 rounded p-1 text-muted hover:bg-hover md:hidden"
          aria-label="Open sidebar"
        >
          <Bars3Icon className="size-5" />
        </button>
        <div className="min-h-0 flex-1 overflow-auto scroll-thin" id="main-scroll">
          {children}
        </div>
      </main>
    </div>
  );
}

export function AppLayout() {
  return (
    <Shell sidebar={<Sidebar />}>
      <Outlet />
    </Shell>
  );
}

export function PageContainer({ children, wide, className }: { children: ReactNode; wide?: boolean; className?: string }) {
  return <div className={cn("mx-auto w-full px-6 pt-12 pb-24 md:px-12", wide ? "max-w-none" : "max-w-5xl", className)}>{children}</div>;
}
